import { useEffect, useState } from "react";

export interface UseTabNavigationResult<Id extends string> {
  /** The destination currently showing. Always a member of `tabs`. */
  activeId: Id;
  /** Select a destination. Pass straight to `BottomNavBar`'s `onSelect`. */
  selectTab: (id: Id) => void;
}

/**
 * Owns "which destination is showing" for a bottom-nav surface.
 *
 * The state itself is trivial; the reason this is shared rather than
 * restated per app is the **disappearing-destination rule**, which is subtle
 * and easy to get silently wrong.
 *
 * A destination can vanish underneath the user. The extension's SMS tab is
 * the live example: it joins the bar only once an SMS envelope has arrived
 * within the current service-worker lifetime, so a worker restart removes it
 * — while the user is parked on it. Left alone, `activeId` then points at a
 * destination the bar no longer lists: nothing renders as active, and the
 * host's own `activeId === "sms"` branch keeps rendering content the user
 * can no longer navigate back to. This hook resolves that by falling back,
 * so the rule has one definition and one test instead of one per surface.
 *
 * The fallback is explicit rather than "first tab" because only the host
 * knows which destination is always present — and the always-present one is
 * not necessarily first once feature flags start removing entries.
 *
 * Deliberately platform-free: no storage, no router, no persistence. Where
 * the value lives is the host's call, and both surfaces have concluded it
 * belongs in memory (a tab has no meaning across sessions, and losing it
 * just means landing on the fallback).
 *
 * @param tabs Destinations currently available. Only `id` is read, so a full
 *   `NavTab[]` can be passed as-is. A non-memoised array is wasteful but not
 *   incorrect: re-setting the same id is a no-op in React.
 * @param fallbackId Destination to land on when `activeId` leaves `tabs`.
 *   Also the initial value.
 */
export function useTabNavigation<Id extends string>(
  tabs: readonly { readonly id: Id }[],
  fallbackId: Id
): UseTabNavigationResult<Id> {
  const [activeId, setActiveId] = useState<Id>(fallbackId);

  // Derived rather than computed inside the effect, so the effect depends on
  // a plain boolean instead of the `tabs` array. Hosts rebuild that array
  // every render, so depending on it directly would re-run this on every
  // render — and depending on a hand-rolled "content key" instead would mean
  // suppressing the exhaustive-deps rule. This needs neither.
  const activeIsListed = tabs.some((tab) => tab.id === activeId);

  useEffect(() => {
    // Re-setting the same id is a no-op in React, so the empty-`tabs` case
    // (where the fallback isn't listed either) settles rather than looping.
    if (!activeIsListed) setActiveId(fallbackId);
  }, [activeIsListed, fallbackId]);

  return { activeId, selectTab: setActiveId };
}
